import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../../services/api';

const CoachClientsModal = ({ isOpen, onClose, coach }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll({ role: 'client', coachId: coach.id });
      setClients(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching coach clients:', err);
      setError('Failed to load clients list');
    } finally {
      setLoading(false);
    }
  }, [coach]);

  useEffect(() => {
    if (isOpen && coach) {
      fetchClients();
    }
  }, [isOpen, coach, fetchClients]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-[var(--border-light)] flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="p-8 border-b border-[var(--border-light)] flex justify-between items-center bg-gradient-to-r from-orange-500/10 to-[var(--surface-white)]">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-tangerine/10 flex items-center justify-center overflow-hidden border-2 border-[var(--surface-white)] shadow-sm">
              {coach?.avatarUrl ? (
                <img src={`${process.env.REACT_APP_API_URL}${coach.avatarUrl}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="material-symbols-outlined text-tangerine text-2xl font-bold">person</span>
              )}
            </div>
            <div>
              <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tight">Clients for {coach?.name}</h2>
              <p className="text-xs font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest mt-1">
                Coach Management • ID: {coach?.id.substring(0, 8)}...
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-[var(--off-white)] flex items-center justify-center text-[var(--text-secondary)] transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-0 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-tangerine border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-[var(--text-secondary)] font-bold">Loading client database...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-red-600 dark:text-red-400">
              <span className="material-symbols-outlined text-4xl mb-2">error</span>
              <p className="font-bold">{error}</p>
              <button onClick={fetchClients} className="mt-4 underline font-bold">Try again</button>
            </div>
          ) : (
            <div className="min-w-full">
              <table className="w-full text-left">
                <thead className="bg-[var(--off-white)]/50">
                  <tr className="border-b border-[var(--border-light)]">
                    <th className="py-4 px-8 font-bold text-[var(--text-secondary)]/50 uppercase text-[10px] tracking-widest">Client Name</th>
                    <th className="py-4 px-4 font-bold text-[var(--text-secondary)]/50 uppercase text-[10px] tracking-widest text-center">Status</th>
                    <th className="py-4 px-8 font-bold text-[var(--text-secondary)]/50 uppercase text-[10px] tracking-widest text-right">Joined At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-light)]/30">
                  {clients.length > 0 ? (
                    clients.map((client) => (
                      <tr key={client.id} className="hover:bg-[var(--off-white)]/50 transition-colors">
                        <td className="py-4 px-8">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 font-bold text-xs border border-[var(--surface-white)]">
                              {client.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-bold text-sm text-[var(--text-primary)]">{client.name}</div>
                              <div className="text-[10px] text-[var(--text-secondary)]/50 font-medium">{client.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`px-3 py-1 rounded-full font-bold text-[9px] uppercase tracking-wider ${
                            client.status === 'blocked' 
                              ? 'bg-red-500/10 text-red-600 dark:text-red-400' 
                              : (client.isWaitlisted ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400' : 'bg-green-500/10 text-green-600 dark:text-green-400')
                          }`}>
                            {client.status === 'blocked' ? 'Blocked' : (client.isWaitlisted ? 'Waitlist' : 'Active')}
                          </span>
                        </td>
                        <td className="py-4 px-8 text-right font-bold text-[var(--text-secondary)]/70 text-xs">
                          {formatDate(client.createdAt)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="py-20 text-center text-[var(--text-secondary)]/50 font-medium italic">
                        No clients linked to this coach yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-[var(--border-light)] bg-[var(--off-white)]/30 text-center">
          <p className="text-[10px] font-bold text-[var(--text-secondary)]/50 uppercase tracking-widest">
            Total linked clients: <span className="text-[var(--text-primary)]">{clients.length}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoachClientsModal;
